import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { AuthService } from '../services/auth.service';
import { JWTTokenServiceService } from '../services/token.service';
import { ToastrService } from 'ngx-toastr';
import { UsersService } from 'src/app/private/services/users.service';
import { TrackingService } from 'src/app/private/services/tracking.service';
import { SessionGuardService } from 'src/app/private/services/session-guard.service';
import { ResumeDataRefreshService } from 'src/app/private/services/resume-data-refresh.service';
import { SubscriptionBillingLockService } from 'src/app/private/services/subscription-billing-lock.service';

import { ThemeService } from 'src/app/private/services/theme.service';
import { PermissionService } from 'src/app/private/services/permission.service';
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  userName: any;
  btnLoading: boolean = false;
  isPasswordVisible = false;

  form: FormGroup = new FormGroup({
    username: new FormControl(''),
    password: new FormControl(''),
  });
  submitted = false;
  isLoading = false;
  buttonTitle = 'Login';
  otpForm: boolean = false;
  userOTP: any;
  loginData: any;

  /** Set when redirected here because another device logged in. */
  sessionInvalidatedMsg: string | null = null;

  constructor(
    private tracker: TrackingService,
    private toastr: ToastrService,
    private localStorageService: LocalStorageService,
    private jwt: JWTTokenServiceService,
    private coreAuth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private userApi: UsersService,
    private sessionGuard: SessionGuardService,
    private resumeDataRefresh: ResumeDataRefreshService,
    private subscriptionBillingLockService: SubscriptionBillingLockService,
    private themeService: ThemeService,
    private permissionService: PermissionService,
  ) {}

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.onEnter();
    }
  }

  onEnter() {
    if (this.otpForm) {
      this.onOTPLogin();
    } else {
      this.onSubmit();
    }
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      username: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(50),
        ],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(40),
        ],
      ],
    });

    // Show a banner if the user was force-logged out due to another device signing in.
    const reason = this.route.snapshot.queryParamMap.get('reason');
    if (reason === 'session_invalidated') {
      this.sessionInvalidatedMsg =
        'You have been logged out because your account was accessed from another device.';
    }
  }

  /**
   * Resolves the post-login destination route.
   *
   * Priority order:
   * 1. Billing lock        → /main/billing-subscription  (server-enforced)
   * 2. HRM access present  → /main/my-desk/my-dashboard
   *    Condition: permissions contain an active 'hrm' or 'my_desk' module entry
   *    (applies to ALL roles — admin, superadmin, regular users, etc.)
   * 3. Default             → /main/home
   */
  private resolveLoginRoute(res: any): string {
    // Priority 1: billing lock enforced server-side
    if (res.billing_route_lock) {
      return '/main/billing-subscription';
    }

    try {
      // Priority 2: any user with HRM / My Desk access goes to My Desk
      const permissions: any[] = JSON.parse(
        this.localStorageService.getPermissions() ?? '[]',
      );

      const hasHrmAccess = permissions.some(
        (p: any) =>
          (p.module_name === 'hrm' || p.module_name === 'my_desk') &&
          p.active === true,
      );

      if (hasHrmAccess) {
        const role = (this.localStorageService.getRole() || '').trim();
        console.log(
          `[Login] Role "${role}" has HRM access → /main/my-desk/my-dashboard`,
        );
        return '/main/my-desk/my-dashboard';
      }
    } catch (e) {
      console.warn(
        '[Login] resolveLoginRoute error (falling back to /main/leads):',
        e,
      );
    }

    // Default: CRM home
    return '/main/leads';
  }

  getAllCenter() {
    const param = {
      where: {
        client_id: this.localStorageService.getClientId(),
      },
    };
    this.userApi.getAllCenter(param).subscribe({
      next: (res) => {
        localStorage.setItem('center_id', res[0]['center_id']);
      },
      error: (e) => {},
    });
  }

  forgetPassword() {
    this.router.navigate(['forget-password']);
  }

  goToDashboard() {
    this.router.navigate(['dashboard/home']);
  }

  get formError(): any {
    return this.form.controls;
  }

  otpSentMessage: string | null = null;
  otpErrorMessage: string | null = null;

  onOTPLogin() {
    this.submitted = true;
    this.otpErrorMessage = null;

    if (!this.userOTP || !this.userOTP.toString().trim()) {
      this.otpErrorMessage = 'Invalid OTP. Please provide the correct OTP.';
      return;
    }

    this.isLoading = true;
    this.buttonTitle = 'Verifying...';
    let param = {
      user_id: this.loginData.user_id,
      otp: this.userOTP,
      token: this.loginData.token,
    };
    this.coreAuth.verifyOtp(param).subscribe({
      next: (res) => {
        if (res) {
          this.buttonTitle = 'Redirecting...';
          this.isLoading = true;
          this.jwt.setToken(res.token);
          this.toastr.success('Login Successful', 'Welcome');
          this.localStorageService.login(res);
          this.sessionGuard.startPolling();
          this.resumeDataRefresh.start();
          this.themeService.init();
          this.tracker.track('Login', 'Authentication', 'User Login OTP', null);
          this.subscriptionBillingLockService.applyLoginSubscriptionLock(res);
          this.subscriptionBillingLockService
            .ensureActivePackageForClient()
            .subscribe();
          this.router
            .navigate([this.resolveLoginRoute(res)])
            .then(() => {
              this.isLoading = false;
              this.buttonTitle = 'Login';
            })
            .catch(() => {
              this.isLoading = false;
              this.buttonTitle = 'Login';
            });
        }
      },
      error: (e) => {
        this.buttonTitle = 'Login';
        this.isLoading = false;
        this.otpErrorMessage = 'Invalid OTP. Please provide the correct OTP.';
        this.toastr.error(
          'Invalid OTP. Please provide the correct OTP.',
          'Authentication Error',
        );
      },
      complete: () => {
        // loading state handled on navigation or error
      },
    });
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.form.invalid) {
      console.log('Form is invalid. Displaying error messages.');
      console.log('Username Errors:', this.form.controls['username'].errors);
      console.log('Password Errors:', this.form.controls['password'].errors);
      return;
    }
    this.isLoading = true;
    this.buttonTitle = 'Loading';
    this.coreAuth.loginUser(this.form.value).subscribe({
      next: (res) => {
        if (res) {
          if (res.twoFactorAuthentication) {
            this.otpForm = true;
            this.loginData = res;
            this.isLoading = false;
            this.buttonTitle = 'Login';
            const emailTarget =
              res.email || res.user_email || res.user?.email || '';
            const emailText = emailTarget
              ? ` to ${emailTarget}`
              : ' to your registered email address';
            this.otpSentMessage = `OTP has been sent${emailText}. Please enter the code below.`;
            this.toastr.success(`OTP has been sent${emailText}`, 'OTP Sent');
          } else {
            this.buttonTitle = 'Redirecting...';
            this.isLoading = true;
            this.jwt.setToken(res.token);
            this.toastr.success('Login Successful', 'Welcome');
            console.log('Login Data', res);
            this.localStorageService.login(res);
            this.sessionGuard.startPolling();
            this.resumeDataRefresh.start();
            this.themeService.init();
            this.tracker.track('Login', 'Authentication', 'User Login', null);
            this.subscriptionBillingLockService.applyLoginSubscriptionLock(res);
            this.subscriptionBillingLockService
              .ensureActivePackageForClient()
              .subscribe();
            this.router
              .navigate([this.resolveLoginRoute(res)])
              .then(() => {
                this.isLoading = false;
                this.buttonTitle = 'Login';
              })
              .catch(() => {
                this.isLoading = false;
                this.buttonTitle = 'Login';
              });
          }
        }
      },
      error: (e) => {
        this.buttonTitle = 'Login';
        this.isLoading = false;
        this.toastr.error('Login Failed', 'Error');
      },
      complete: () => {
        // loading state handled on navigation or error
      },
    });
  }
  resetToLogin(): void {
    this.otpForm = false;
    this.otpSentMessage = null;
    this.otpErrorMessage = null;
    this.userOTP = '';
  }

  forgetPass() {
    this.router.navigate(['forget']);
  }

  private landingRoute(billingLock: boolean): string {
    if (billingLock) return '/main/billing-subscription';
    this.permissionService.refresh();
    if (this.permissionService.hasNavAccess('hrm'))
      return '/main/my-desk/my-dashboard';
    if (this.permissionService.hasNavAccess('home')) return '/main/home';
    if (this.permissionService.hasNavAccess('lead')) return '/main/leads';

    return '/main/leads';
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }
}
